import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { take } from 'rxjs';
import { MatMenuTrigger } from '@angular/material/menu';

import { IFilterNumber } from '@shared/models/graphql';

import { Store } from '@ngrx/store';
import { AppState } from '@state/app.state';
import { selectFiltersCurrent } from '@state/filters/filters.selectors';
import { setUnsavedPriceFiltersAction } from '@state/filters/filters.actions';

export const MAX_PRICE = 600000;

@UntilDestroy()
@Component({
  selector: 'app-filter-v2-price',
  templateUrl: './filter-v2-price.component.html',
  styleUrls: [
    './filter-v2-price.component.scss',
    '../filters-v2.component.scss',
  ],
})
export class FilterV2PriceComponent implements OnInit {
  @Input() mobile = false;
  @ViewChild('inputMin') inputMin?: ElementRef<HTMLInputElement>;
  @ViewChild('inputMax') inputMax?: ElementRef<HTMLInputElement>;
  @ViewChild('trigger') menuTrigger!: MatMenuTrigger;
  @Output() applyFilters = new EventEmitter();

  protected isOpen = false;
  protected label = 'Any price';
  public minValue: number | null = null;
  public maxValue: number | null = null;

  constructor(private store: Store<AppState>) {}

  ngOnInit() {
    // for loading the first time
    this.store
      .select(selectFiltersCurrent)
      .pipe(take(1))
      .subscribe((filters) => {
        if (filters?.price) {
          if (filters.price.eq) {
            this.minValue = this.maxValue = filters.price.eq;
          } else {
            if (filters.price.greaterThanOrEqual) {
              this.minValue = filters.price.greaterThanOrEqual;
            }
            if (filters.price.lessThanOrEqual) {
              this.maxValue = filters.price.lessThanOrEqual;
            }
          }
          this.handleLabel();
        } else {
          this.minValue = null;
          this.maxValue = null;
        }
      });

    // for clearing the filters
    this.store
      .select(selectFiltersCurrent)
      .pipe(untilDestroyed(this))
      .subscribe((filters) => {
        if (!filters?.price && (this.minValue || this.maxValue)) {
          this.minValue = null;
          this.maxValue = null;
          this.handleLabel();
        }
      });
  }

  onInputChange() {
    let priceQuery: IFilterNumber | undefined = {};

    if (this.minValue && this.maxValue && this.minValue > this.maxValue) {
      this.maxValue = this.minValue;
    }

    if (!this.minValue && !this.maxValue) {
      priceQuery = undefined;
    } else if (
      this.minValue &&
      this.maxValue &&
      this.minValue === this.maxValue
    ) {
      priceQuery = { eq: this.minValue };
    } else {
      if (this.minValue && this.minValue > 0) {
        priceQuery = { greaterThanOrEqual: this.minValue };
      }
      if (this.maxValue && this.maxValue > 0) {
        priceQuery = { ...priceQuery, lessThanOrEqual: this.maxValue };
      }
    }

    this.handleLabel();
    this.store.dispatch(setUnsavedPriceFiltersAction({ ...priceQuery }));
    if (!this.mobile) this.applyFilters.emit();
  }

  handleLabel() {
    if (!this.minValue && !this.maxValue) {
      this.label = 'Any price';
    } else {
      const minFormatted = (this.minValue || 0).toLocaleString('en-US');
      const maxFormatted = (this.maxValue || 0).toLocaleString('en-US');

      if (this.minValue === this.maxValue) {
        this.label = `$${maxFormatted}`;
      } else if (this.minValue && !this.maxValue) {
        this.label = `$${minFormatted}+`;
      } else if (this.maxValue && !this.minValue) {
        this.label = `Up to $${maxFormatted}`;
      } else {
        this.label = `$${minFormatted} - $${maxFormatted}`;
      }
    }
  }

  loadInitialValue() {
    this.store
      .select(selectFiltersCurrent)
      .pipe(take(1))
      .subscribe((filters) => {
        if (filters?.price) {
          if (filters.price.eq) {
            this.minValue = this.maxValue = filters.price.eq;
          } else {
            if (filters.price.greaterThanOrEqual) {
              this.minValue = filters.price.greaterThanOrEqual;
            } else {
              this.minValue = null;
            }
            if (filters.price.lessThanOrEqual) {
              this.maxValue = filters.price.lessThanOrEqual;
            } else {
              this.maxValue = null;
            }
          }
          this.handleLabel();
        } else {
          this.minValue = null;
          this.maxValue = null;
        }
      });
  }

  resetPrice() {
    this.minValue = null;
    this.maxValue = null;
    this.handleLabel();
    this.store.dispatch(setUnsavedPriceFiltersAction({}));
    this.applyFilters.emit();
  }

  closeMenu(): void {
    this.menuTrigger.closeMenu();
  }

  selectAll(event: FocusEvent): void {
    const input = event.target as HTMLInputElement;
    input.addEventListener('contextmenu', (e) => {
      e.preventDefault();
    });
    input.select();
  }
}
